// @ts-strict-ignore
import capitalize from 'lodash/capitalize';
import type { Option } from '@usealloy/component-library';
import type { ServiceConfigForm, ServiceConfig, ServiceConfigOptionsMapping } from '../types';
import { DefaultLevel } from '../types';
import { UnknowOption, AlloyOption, CustomerOption } from './fixture';

export const formatValue = (option, optionMetaData): Option | string | Option[] => {
  if (!optionMetaData) {
    return '';
  }
  if (['string', 'number', 'decimal'].includes(optionMetaData.datatype)) {
    if (!option) {
      return '';
    }
    return option.value;
  }
  if (optionMetaData.datatype === 'boolean') {
    if (!option) {
      return {} as Option;
    }
    return {
      label: capitalize(option.value || ''),
      value: (option.value || '').toLowerCase(),
    } as Option;
  }
  if (!option) {
    return [];
  }

  // Create a map for quick lookup of possible values by their ID
  const possibleValuesMap = optionMetaData.possibleValues?.reduce((acc, pv) => {
    acc[pv.serviceConfigPossibleValueId] = pv;
    return acc;
  }, {}) || {};

  // Preserve the original order of selectedValues when creating the result
  return option.selectedValues
    ?.map(selectedValueId => {
      const possibleValue = possibleValuesMap[selectedValueId];
      if (!possibleValue) {
        return null; // Skip invalid selected values
      }
      return {
        label: possibleValue.displayName,
        value: possibleValue.serviceConfigPossibleValueId.toString(),
      } as Option;
    })
    .filter(Boolean); // Remove null values
};

export const getCredentialOwnerOption = credentialOwner => {
  if (credentialOwner === 'customer') {
    return CustomerOption;
  }
  if (credentialOwner === 'alloy') {
    return AlloyOption;
  }
  return UnknowOption;
};

export const convertCustomerServiceConfig = (
  service: ServiceConfig,
): {
  serviceConfigOptionsMapping?: ServiceConfigOptionsMapping;
  serviceConfigForm?: ServiceConfigForm;
} => {
  if (service === undefined) {
    return {
      serviceConfigOptionsMapping: {},
      serviceConfigForm: {
        credentialOwnerOption: null,
        formattedServiceConfigOptions: [],
      },
    };
  }
  const serviceConfigOptionsMapping = service.serviceConfigOptions?.reduce((acc, option) => {
    acc[option.serviceConfigOptionId] = option;
    return acc;
  }, {});

  const customerCredentialOwner = service.selectedCustomerOptions.credentialOwner;
  let credentialOwnerOption = UnknowOption;

  const optionMap = new Map();
  service.selectedCustomerOptions?.serviceConfigOptions?.forEach(option => {
    const optionMetaData = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    const value = formatValue(option, optionMetaData);
    optionMap.set(option.serviceConfigOptionId, {
      serviceConfigOptionId: option.serviceConfigOptionId,
      value,
      isUsingDefault: false,
      defaultLevel: DefaultLevel.NULL,
    });
    // If there is a workflow-level owner field, we set the credential owner by using the
    // credential owner at the customer-level.
    if (optionMetaData.isOwner) {
      credentialOwnerOption = getCredentialOwnerOption(customerCredentialOwner);
    }
  });

  service.selectedAlloyOptions?.serviceConfigOptions?.forEach(option => {
    const optionMetaData = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    const value = formatValue(option, optionMetaData);
    if (optionMap.has(option.serviceConfigOptionId)) {
      optionMap.get(option.serviceConfigOptionId).defaultValue = value;
      optionMap.get(option.serviceConfigOptionId).defaultLevel = DefaultLevel.ALLOY;
    } else if (optionMetaData.isRequired) {
      optionMap.set(option.serviceConfigOptionId, {
        serviceConfigOptionId: option.serviceConfigOptionId,
        value,
        defaultValue: value,
        isUsingDefault: true,
        defaultLevel: DefaultLevel.ALLOY,
      });
      // If the owner field is NOT found at the customer level but found at the alloy level,
      // we overwrite the credential owner to the alloy-level credential owner.
      if (optionMetaData.isOwner) {
        credentialOwnerOption = AlloyOption;
      }
    }
  });

  service.serviceConfigOptions?.forEach(optionMetaData => {
    const value = formatValue(null, optionMetaData);
    // If the field is required and we don't have value in any level, we place a empty filed.
    if (!optionMap.has(optionMetaData.serviceConfigOptionId) && optionMetaData.isRequired) {
      optionMap.set(optionMetaData.serviceConfigOptionId, {
        serviceConfigOptionId: optionMetaData.serviceConfigOptionId,
        value,
        isUsingDefault: false,
        defaultLevel: DefaultLevel.NULL,
      });
    }
  });

  return {
    serviceConfigOptionsMapping,
    serviceConfigForm: {
      credentialOwnerOption,
      formattedServiceConfigOptions: Array.from(optionMap.values()),
    },
  };
};

export const formatServiceConfigUpdateInput = ({ data, serviceConfigOptionsMapping }) => {
  const serviceConfigOptions = data.formattedServiceConfigOptions.reduce((acc, option) => {
    // if using default, we ignore it
    if (option.isUsingDefault) {
      return acc;
    }
    const serviceConfigMeta = serviceConfigOptionsMapping[option.serviceConfigOptionId];
    const selectedValues = Array.isArray(option.value)
      ? option.value.map(v => parseInt(v.value, 10))
      : null;
    const selectedSlugs = Array.isArray(selectedValues)
      ? serviceConfigMeta.possibleValues
        .filter(v => selectedValues.includes(v.serviceConfigPossibleValueId))
        .map(v => v.slug)
        .join(',')
      : null;
    let value;
    if (option.value instanceof Object && option.value.constructor === Object) {
      // If the option.value is a object, which means it is a boolean selector option.
      // we need to take options value.
      value = option.value.value;
    } else if (Array.isArray(option.value)) {
      // If the option.value is an array, we set the value to null. selectedValues will handle
      // the multi-selector.
      value = null;
    } else {
      value = option.value;
    }
    acc.push({
      slug: serviceConfigMeta.slug,
      isOwner: serviceConfigMeta.isOwner,
      isSecret: serviceConfigMeta.isSecret,
      serviceConfigOptionId: option.serviceConfigOptionId,
      value,
      selectedValues,
      selectedSlugs,
    });
    return acc;
  }, []);
  return {
    credentialOwner: data.credentialOwnerOption.value,
    serviceConfigOptions,
    serviceConfigOptionsMapping,
  };
};
